import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Techpack() {
  const navigate = useNavigate();
  const [techpacks, setTechpacks] = useState([]);
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editTechpack, setEditTechpack] = useState(null);
  const [formData, setFormData] = useState({
    article_id: '',
    article_name: '',
    version: '',
    specifications: {},
    measurements: {},
    materials: []
  });

  useEffect(() => {
    fetchTechpacks();
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data?entity_type=article`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch articles');
    }
  };

  const fetchTechpacks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/techpacks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTechpacks(response.data);
    } catch (error) {
      toast.error('Failed to fetch techpacks');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editTechpack) {
        const payload = { ...formData, id: editTechpack.id };
        await axios.put(`${API}/techpacks/${editTechpack.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/techpacks`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Created successfully');
      }
      setOpen(false);
      setEditTechpack(null);
      setFormData({ article_id: '', article_name: '', version: '', specifications: {}, measurements: {}, materials: [] });
      fetchTechpacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/techpacks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      fetchTechpacks();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Techpack</h1>
              <p className="text-sm text-gray-600">Technical specifications</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex justify-end mb-6">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditTechpack(null); setFormData({ article_id: '', article_name: '', version: '', specifications: {}, measurements: {}, materials: [] }); }} data-testid="add-techpack-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Techpack
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="techpack-dialog">
              <DialogHeader>
                <DialogTitle>{editTechpack ? 'Edit Techpack' : 'Create Techpack'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Article</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.article_id}
                    onChange={(e) => {
                      const article = articles.find(a => a.id === e.target.value);
                      setFormData({ ...formData, article_id: e.target.value, article_name: article?.name || '' });
                    }} required data-testid="article-select">
                    <option value="">Select Article</option>
                    {articles.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Version</Label>
                  <Input value={formData.version} onChange={(e) => setFormData({ ...formData, version: e.target.value })} required data-testid="version-input" placeholder="v1.0" />
                </div>
                <div className="space-y-2">
                  <Label>Specifications (JSON)</Label>
                  <textarea className="w-full px-3 py-2 border rounded-md font-mono text-sm" rows="4"
                    value={JSON.stringify(formData.specifications, null, 2)}
                    onChange={(e) => {
                      try {
                        setFormData({ ...formData, specifications: JSON.parse(e.target.value) });
                      } catch {}
                    }}
                    placeholder='{"fabric": "cotton", "color": "blue"}'
                    data-testid="specifications-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-techpack">{editTechpack ? 'Update' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {techpacks.map((techpack) => (
            <div key={techpack.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow" data-testid={`techpack-card-${techpack.id}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-lg">{techpack.article_name}</h3>
                  <p className="text-sm text-gray-500">Version: {techpack.version}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => { setEditTechpack(techpack); setFormData(techpack); setOpen(true); }} data-testid={`edit-techpack-${techpack.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(techpack.id)} data-testid={`delete-techpack-${techpack.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                <p className="font-medium mb-1">Specifications:</p>
                <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-32">{JSON.stringify(techpack.specifications, null, 2)}</pre>
              </div>
            </div>
          ))}
        </div>
        {techpacks.length === 0 && <div className="text-center py-12" data-testid="no-techpacks"><p className="text-gray-500">No techpacks found</p></div>}
      </main>
    </div>
  );
}
